import DashboardLayout from "../../../lib/components/layouts/dashboard";
import { FC, useContext, useEffect, useState } from "react";
import { AppContext } from "lib/contexts/app-context";
import { isPatientUser } from "lib/utils/util";
import { PAGE_ROUTES } from "lib/constants/page-routes";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import SimpleCard from "lib/components/common/simple-card";
import { Row, Col } from "react-bootstrap";
import SimpleButton from "lib/components/common/simple-button";
import SimpleModal from "lib/components/common/simple-modal";
import LoadingBox from "lib/components/common/loading-box";
import { toast } from "sonner";
import usePackages from "lib/hooks/usePackages";
import usePatientBookings from "lib/hooks/usePatientBookings";
import useLogging from "lib/hooks/useLogging";
import {
  formatDate,
  formatDateWithTime,
  getLocalISO,
} from "lib/utils/date-utils";
import useApi from "lib/hooks/useApi";


interface PatientCheckoutSuccessPageProps {}

const PatientCheckoutSuccessPage: FC<PatientCheckoutSuccessPageProps> = ({}) => {
  const { appState } = useContext(AppContext);
  const [searchParams] = useSearchParams();
  const navigateTo = useNavigate();
  const location = useLocation();

  // Get booking details from navigation state
  const bookingDetails = location.state?.bookingDetails || location.state?.originalBookingDetails;

  // Debug: Log the location state to see what's being passed

  const BreadcrumbItems = [
    {
      label: "Dashboard",
      path: isPatientUser(appState?.userRole)
        ? PAGE_ROUTES.PATIENT.Dashboard
        : PAGE_ROUTES.PRACTITIONER.Dashboard,
    },
    {
      label: "Booking Success",
      path: PAGE_ROUTES.PATIENT.BookSessionSuccess,
    },
  ] as BreadcrumbItem[];

  const handleGoToDashboard = () => {
    navigateTo(isPatientUser(appState?.userRole) ? PAGE_ROUTES.PATIENT.Dashboard : PAGE_ROUTES.ADMIN.Dashboard);
  };

  const handleBookAnother = () => {
    navigateTo(isPatientUser(appState?.userRole) ? PAGE_ROUTES.PATIENT.BookTherapySession : PAGE_ROUTES.ADMIN.PatientsManagement);
  };

  const formatDateTime = (dateTimeString: string) => {
    if (!dateTimeString) return null;
    const date = new Date(dateTimeString);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  const { logItems, write2Log, lastLogItem } = useLogging({
    content: "Please wait ...",
    timestamp: formatDateWithTime(getLocalISO(new Date())),
  });

  const {
      markBuyBookingSessionAsPaid,
      loading: patientLoading,
      error: patientError,
  } = usePatientBookings();
  

  const appointmentDateTime = bookingDetails?.fromTime ? formatDateTime(bookingDetails.fromTime) : null;

  useEffect(() => {
    const transactionId = searchParams.get("transactionId");
    console.log("Transaction ID:", transactionId);
    if (transactionId) {
      write2Log("Marking Buy Package as paid ...");
      markBuyBookingSessionAsPaid(appState?.user_id, transactionId, () => {
        //navigateTo(PAGE_ROUTES.PRACTITIONER.Dashboard);
        write2Log("You are all set ...");
      });
    }
  }, []);

  return (
    <DashboardLayout breadcrumbItems={BreadcrumbItems}>
      <SimpleCard>
        <Row className="text-center">
          <Col>
            <div className="mb-4">
              <i className="fas fa-check-circle text-success" style={{ fontSize: "4rem" }}></i>
            </div>
            <h2 className="text-success mb-3">Session Booked Successfully!</h2>
            <p className="text-muted mb-4">
              Your Session Booked has been confirmed. You will receive a confirmation email shortly.
            </p>
            
            <div className="d-flex justify-content-center gap-3">
              <SimpleButton
                onClick={handleGoToDashboard}
                variant="outline-primary"
                size="lg"
              >
                Go to Dashboard
              </SimpleButton>
              <SimpleButton
                onClick={handleBookAnother}
                variant="primary"
                size="lg"
              >
                Book another therapy session
              </SimpleButton>
            </div>
          </Col>
        </Row>
        <SimpleModal
          open={patientLoading}
          close={() => {}}
          size="sm"
        >
          <LoadingBox message={lastLogItem.content} />
        </SimpleModal>
      </SimpleCard>
    </DashboardLayout>
  );
};

export default PatientCheckoutSuccessPage;
